/**
 * LookEditorModal.js — the character editor, in-app.
 *
 * Ported from the website's character editor (kickviz-express
 * views/partials/character-editor.ejs + public/javascripts/character-editor.js)
 * so the React app offers the same thing: a live 3D figure wearing THIS team's
 * kit, filters that narrow 200 skins and 74 hairstyles down to a handful you
 * can actually see, and Apply / Cancel.
 *
 * Why not a <select>: the style catalog is 200 skins × 74 hairs. A dropdown of
 * 200 ids is unusable on a phone and tells you nothing about the result. The
 * filters (face → tone → eyes, cut → colour) plus a thumbnail grid are what
 * makes the choice visible, and the figure answers the question the label
 * cannot: "what does this look like on the pitch?"
 *
 * Props:
 * - player:  the roster entry being edited ({ name, number, position, skinStyle, hairStyle })
 * - team:    the team draft — supplies kit textures + numberColor for the preview
 * - styles:  { skinStyles, hairStyles } from uploadsAPI.styles()
 * - onClose: () => void
 * - onApply: ({ skinStyle, hairStyle }) => void
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { getMediaUrl } from './api';
import { createPreview } from './characterEditorPreview';
import { useI18n } from './i18n';

const btn = (bg, fg = '#fff') => ({
  padding: '8px 13px', border: 'none', borderRadius: 6, cursor: 'pointer',
  background: bg, color: fg, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
});
const field = {
  width: '100%', boxSizing: 'border-box', padding: '6px 8px',
  background: '#0f172a', border: '1px solid #475569', borderRadius: 6,
  color: '#e2e8f0', fontSize: 12,
};
const h3 = { fontSize: 11, color: '#94a3b8', margin: '10px 0 4px', textTransform: 'uppercase', letterSpacing: 0.4 };

// The catalog is versioned; thumbnails get the same cache-buster the website
// uses so a re-cut style does not serve a stale jpg.
const THUMB_V = 'avatar-picker-v24';
const thumbUrl = (style) => {
  if (!style?.thumb) return null;
  const url = getMediaUrl(style.thumb);
  return `${url}${url.includes('?') ? '&' : '?'}v=${THUMB_V}`;
};

const uniq = (arr) => [...new Set(arr.filter(v => v != null && v !== ''))];

export default function LookEditorModal({ player, team, styles, onClose, onApply }) {
  const { t } = useI18n();

  // Sorted the way the website sorts: skins by tone order (light → dark runs as
  // a gradient across the grid), hairs in catalog order.
  const skins = useMemo(
    () => [...(styles?.skinStyles || [])].sort((a, b) => (a.toneOrder ?? 99) - (b.toneOrder ?? 99)),
    [styles],
  );
  const hairs = useMemo(() => styles?.hairStyles || [], [styles]);

  const fallbackSkin = skins[0]?.id || 'style_000';
  const fallbackHair = hairs[0]?.id || 'style_000';
  const [skinStyle, setSkinStyle] = useState(player?.skinStyle || fallbackSkin);
  const [hairStyle, setHairStyle] = useState(player?.hairStyle || fallbackHair);

  const skin = skins.find(s => s.id === skinStyle) || skins[0];
  const hair = hairs.find(h => h.id === hairStyle) || hairs[0];

  // ── Filters: each one narrows the grid, and picking a filter keeps the other
  // attributes you already chose when a match exists (the website's rule). ──
  const faceStyles = useMemo(() => uniq(skins.map(s => s.faceStyle)), [skins]);
  const faces = useMemo(
    () => skins.filter(s => s.faceStyle === (skin?.faceStyle ?? null)),
    [skins, skin],
  );
  const tones = useMemo(() => uniq(faces.map(s => s.tone)), [faces]);
  const eyes = useMemo(
    () => uniq(faces.filter(s => s.tone === skin?.tone).map(s => s.eyeOption)).sort(),
    [faces, skin],
  );
  const cuts = useMemo(() => uniq(hairs.map(h => h.cut)), [hairs]);
  const colors = useMemo(
    () => uniq(hairs.filter(h => h.cut === hair?.cut).map(h => h.color)),
    [hairs, hair],
  );

  const pickFace = (face) => {
    const matches = skins.filter(s => s.faceStyle === face && s.tone === skin?.tone);
    setSkinStyle((matches.find(s => s.eyeOption === skin?.eyeOption) || matches[0] || skins.find(s => s.faceStyle === face))?.id);
  };
  const pickTone = (tone) => {
    const matches = skins.filter(s => s.faceStyle === skin?.faceStyle && s.tone === tone);
    setSkinStyle((matches.find(s => s.eyeOption === skin?.eyeOption) || matches[0])?.id);
  };
  const pickEyes = (eyeOption) => {
    setSkinStyle(skins.find(s => s.faceStyle === skin?.faceStyle && s.tone === skin?.tone && s.eyeOption === eyeOption)?.id);
  };
  const pickCut = (cut) => {
    const matches = hairs.filter(h => h.cut === cut);
    setHairStyle((matches.find(h => h.color === hair?.color) || matches[0])?.id);
  };
  const pickColor = (color) => {
    setHairStyle(hairs.find(h => h.cut === hair?.cut && h.color === color)?.id);
  };

  // One representative per tone (skins) / per cut (hairs) — otherwise the grid
  // is 200 thumbnails deep and the choice hides in the scroll.
  const skinChoices = useMemo(() => {
    const pool = faces.length ? faces : skins;
    return uniq(pool.map(s => s.tone)).map(tone => {
      const matching = pool.filter(s => s.tone === tone);
      return matching.find(s => s.id === skin?.id) || matching.find(s => s.eyeOption === skin?.eyeOption) || matching[0];
    }).filter(Boolean);
  }, [faces, skins, skin]);
  const hairChoices = useMemo(() => uniq(hairs.map(h => h.cut)).map(cut => {
    const matching = hairs.filter(h => h.cut === cut);
    return matching.find(h => h.id === hair?.id) || matching.find(h => h.color === hair?.color) || matching[0];
  }).filter(Boolean), [hairs, hair]);

  // ── Live preview: the same renderer the pitch uses, so what you pick here is
  // what renders there. Debounced like the website (100 ms) because every
  // change re-loads two textures and a hair mesh. ──
  const stageRef = useRef(null);
  const previewRef = useRef(null);
  const [status, setStatus] = useState('');
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    let pv = null;
    try { pv = createPreview(el); } catch (e) { console.error('Look preview failed:', e); }
    previewRef.current = pv;
    return () => { pv?.destroy(); previewRef.current = null; };
  }, [!!(skin && hair)]);
  useEffect(() => {
    const pv = previewRef.current;
    if (!pv || !skin || !hair) return undefined;
    let live = true;
    setStatus(t('look.loading'));
    const timer = setTimeout(async () => {
      try {
        await pv.update({
          name: player?.name || '', number: player?.number || '', position: player?.position || '',
          skinStyle: skin.id, hairStyle: hair.id,
          numberColor: team?.numberColor || '#ffffff',
          teamTextures: {
            shirtFront: team?.shirtFront || '', shirtBack: team?.shirtBack || '',
            shortsFront: team?.shortsFront || '', shortsBack: team?.shortsBack || '',
            socksFront: team?.socksFront || '',
            gkShirtFront: team?.gkShirtFront || '', gkShirtBack: team?.gkShirtBack || '',
            gkShortsFront: team?.gkShortsFront || '', gkShortsBack: team?.gkShortsBack || '',
            gkSocksFront: team?.gkSocksFront || '',
          },
        });
        if (live) setStatus('');
      } catch (e) {
        // The thumbnails still work without WebGL — never strand the choice.
        if (live) setStatus(t('look.noPreview'));
      }
    }, 100);
    return () => { live = false; clearTimeout(timer); };
  }, [skin, hair, player, team, t]);

  // Esc closes this editor only — the team modal behind it stays open.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const swatch = (style, active, kind) => (
    <button
      key={style.id}
      type="button"
      aria-pressed={String(active)}
      title={style.label}
      aria-label={t('look.swatchAria', { label: kind === 'hair' ? style.cut : style.tone })}
      onClick={() => (kind === 'skin' ? setSkinStyle(style.id) : setHairStyle(style.id))}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
        padding: 3, cursor: 'pointer', borderRadius: 6,
        border: active ? '2px solid #38bdf8' : '1px solid #334155',
        background: active ? '#0c4a6e' : '#0f172a', color: '#cbd5e1', fontSize: 9.5,
        width: 62,
      }}
    >
      {thumbUrl(style) ? (
        <img src={thumbUrl(style)} alt="" loading="lazy"
          style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 4, background: '#020617' }} />
      ) : (
        <span style={{ width: 44, height: 44, borderRadius: 4, background: '#020617', display: 'block' }} />
      )}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 58 }}>
        {kind === 'hair' ? style.cut : style.tone}
      </span>
    </button>
  );

  const selectBox = (value, options, onChange, ariaLabel) => (
    <select style={field} value={value || ''} onChange={e => onChange(e.target.value)} aria-label={ariaLabel}>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.72)',
        display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 8,
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        role="dialog" aria-modal="true" aria-label={t('look.title')}
        style={{
          background: '#1e293b', borderRadius: 12, padding: '0.9rem 1.1rem',
          width: '100%', maxWidth: 760, color: '#e2e8f0',
          maxHeight: 'calc(var(--kv-viewport-h) * 0.92)', display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.05rem' }}>
              {t('look.title')} — {player?.name || t('teams.player')} {player?.number ? `· ${player.number}` : ''}
            </h2>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>{t('look.hint')}</div>
          </div>
          <button onClick={onClose} title="Esc" style={{
            background: '#475569', border: 'none', borderRadius: 6, color: '#fff',
            width: 30, height: 30, cursor: 'pointer',
          }}>✕</button>
        </div>

        {!skins.length || !hairs.length ? (
          <div style={{ padding: '18px 0', fontSize: 12, color: '#94a3b8' }}>{t('look.none')}</div>
        ) : (
          <div style={{ display: 'flex', gap: 14, marginTop: 10, flex: '1 1 auto', minHeight: 0 }}>
            {/* ── The figure, in this team's kit ── */}
            <div style={{ width: 250, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div ref={stageRef} style={{
                flex: '1 1 auto', minHeight: 260, borderRadius: 8, overflow: 'hidden',
                border: '1px solid #334155', background: '#d8e0e8',
              }} />
              <div style={{ fontSize: 10.5, color: status ? '#f87171' : '#64748b', minHeight: 15 }}
                role="status">{status || t('look.dragRotate')}</div>
              <div style={{ fontSize: 11, color: '#cbd5e1' }}>
                {t('look.current', { skin: skin?.label || skinStyle, hair: hair?.label || hairStyle })}
              </div>
            </div>

            {/* ── Filters + grids ── */}
            <div style={{ flex: '1 1 auto', minWidth: 0, overflowY: 'auto', paddingRight: 4 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {selectBox(skin?.faceStyle, faceStyles, pickFace, t('teams.skin'))}
                {selectBox(skin?.tone, tones, pickTone, t('teams.skin'))}
                {selectBox(skin?.eyeOption, eyes, pickEyes, t('teams.skin'))}
                {selectBox(hair?.cut, cuts, pickCut, t('teams.hair'))}
                {selectBox(hair?.color, colors, pickColor, t('teams.hair'))}
              </div>

              <h3 style={h3}>{t('teams.skin')}</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {skinChoices.map(s => swatch(s, s.id === skin?.id, 'skin'))}
              </div>

              <h3 style={h3}>{t('teams.hair')}</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {hairChoices.map(h => swatch(h, h.id === hair?.id, 'hair'))}
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end', alignItems: 'center' }}>
          <span style={{ fontSize: 10.5, color: '#64748b', marginRight: 'auto' }}>{t('look.needSave')}</span>
          <button type="button" onClick={onClose} style={btn('#475569')}>{t('look.cancel')}</button>
          <button
            type="button"
            disabled={!skin || !hair}
            onClick={() => onApply({ skinStyle: skin.id, hairStyle: hair.id })}
            style={{ ...btn('#16a34a'), opacity: skin && hair ? 1 : 0.4 }}
          >{t('look.apply')}</button>
        </div>
      </div>
    </div>
  );
}
